import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { Hooks, Utils } from '@platform/maplibre';
import { useSystemPalettes } from '@platform/shared/hooks';
import { Buttons, Popup } from '@platform/shared/ui';
import { ProjectTypes } from '@platform/types';
import { Map } from 'maplibre-gl';
import React, { useState } from 'react';
import { useSessionContext } from '../../../../contexts/SessionContext';
import { figureOutCutPoints } from './utils';

const { useAutoAdjust } = Hooks;

export interface Props {
  workingLayer: ProjectTypes.SocialExplorerWorkingLayer;
  onChange: (updatedRendering: ProjectTypes.ShadedRenderingStyle) => void;
  map: Map | null;
}

const SocialExplorerWorkingLayerAutoAdjust: React.FC<Props> = ({ workingLayer, map, onChange }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { get: getPalettes } = useSystemPalettes();
  const { notify } = useSessionContext();

  const { metadata, variableSelection, rendering } = workingLayer;
  const { surveyName, datasetAbbreviation, isUserSurvey } = metadata;
  const { colorPalette } = rendering.style as ProjectTypes.ShadedRenderingStyle;

  const { autoAdjust, isAllowed } = useAutoAdjust({
    map,
    surveyName,
    datasetAbbreviation,
    variableSelection,
    sourceLayerId: Utils.getWorkingLayerId(workingLayer),
  });

  const handleAutoAdjust = async () => {
    try {
      setIsLoading(true);
      const steps = await autoAdjust();

      if (!steps?.length) {
        notify({ content: 'Failed to auto-adjust. Steps not available.' });
        return;
      }

      const systemPalettes = await getPalettes(surveyName);

      const currentPalette =
        systemPalettes.find((x) => x.id === colorPalette.id && x.type === colorPalette.type) ?? systemPalettes[0];

      const seVarSelection: ProjectTypes.VariableSelection[] = variableSelection.map((x) => ({
        variableGuid: x.variableId,
        tableGuid: x.tableId,
        universeVariableUuid: x.universeVariableId,
        isUserSurvey,
        surveyName,
        datasetAbbrev: datasetAbbreviation,
      }));

      const newCutPoints = await figureOutCutPoints(seVarSelection, steps, currentPalette);

      if (!newCutPoints) {
        notify({ content: "Failed to auto-adjust. Can't figure out new data theme" });
        return;
      }

      onChange({ ...(rendering.style as ProjectTypes.ShadedRenderingStyle), cutPointSets: newCutPoints });
    } catch {
      notify({ content: 'Failed to auto-adjust. ' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAllowed) return null;

  return (
    <>
      <Buttons.Soft
        className="bg-transparent text-xs shadow-none"
        onClick={() => handleAutoAdjust()}
        disabled={isLoading}
      >
        {isLoading ? 'ADJUSTING...' : 'AUTO ADJUST'}
      </Buttons.Soft>
      <Popup
        trigger={
          <div>
            <InformationCircleIcon width={20} className="stroke-2 text-gray-500" />
          </div>
        }
        content={
          <span className="select-none text-xs text-gray-700">
            <b>Auto adjust</b> calculates the ranges based on the currently visible part of the map. We use 8 cut points
            and the natural breaks classification method (K-means).
          </span>
        }
        vAlignment="top-bottom"
      />
    </>
  );
};
export default SocialExplorerWorkingLayerAutoAdjust;
